import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import debounce from 'lodash/debounce';

import { GetStaffResponse, ListStaffRequest } from '@vyce/core/src/api/types';
import { StaffListItem } from '@vyce/core/src/types';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

type Props = {
  selectedCompanyId: string;
  getStaffRequest?: (
    companyId: string,
    payload: ListStaffRequest
  ) => Promise<AxiosResponse<GetStaffResponse>>;
};

export const useFetchPayScheduleManagerCandidates = ({ selectedCompanyId, getStaffRequest }: Props) => {
  const [staff, setStaff] = useState<{ uuid: string; name: string; existing: StaffListItem }[]>([]);
  const [staffSubstring, setStaffSubstring] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const { handleServerError } = useContext(NotificationContext);

  const fetchStaff = useCallback(
    async (substring: string) => {
      if (!selectedCompanyId || !getStaffRequest) return;
      try {
        setLoading(true);
        const { data } = await getStaffRequest(selectedCompanyId, {
          limit: 1000,
          offset: 0,
          substring,
          roles: ['Payroll:Manager'],
        });
        setStaff(
          data.items.map(item => ({
            uuid: item.user_id,
            name: `${item.first_name} ${item.last_name}`,
            existing: item,
          }))
        );
        setLoading(false);
      } catch (e) {
        setLoading(false);
        handleServerError(e);
      }
    },
    [selectedCompanyId, getStaffRequest, handleServerError]
  );

  const debouncedFetchStaff = useMemo(() => debounce(fetchStaff, 500), [fetchStaff]);

  useEffect(() => {
    debouncedFetchStaff(staffSubstring);
  }, [staffSubstring, debouncedFetchStaff]);

  return { staff, setStaffSubstring, loading };
};
